import graph from "./graph";
import React, { useEffect, useState } from "react";

import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import {
  Card,
  Chip,
  Stack,
  styled,
  Switch,
  CardHeader,
  Typography,
  CardActionArea,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { isEmptyObject } from "helpers/formatObject";
import { useMutation, useLazyQuery } from "@apollo/client";
import { addCommas, digitsFaToEn } from "@persian-tools/persian-tools";

export default function Price({ id }) {
  const [value, setValue] = useState(0);
  const [updated, setUpdated] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const { userToken } = useSelector((state) => state.auth);
  const [fetchModel, { loading }] = useLazyQuery(graph.get.query);
  const [updateModel, { loading: updating }] = useMutation(graph.create.query);

  const onClick = () => setDisabled((prev) => !prev);
  const onChange = (e) => setValue(Number(e.target.value));

  const getModel = async () => {
    try {
      const { data } = await fetchModel({
        context: {
          serviceName: graph.get.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          action_ids: id,
        },
      });
      if (!isEmptyObject(data)) {
        const res = data[graph.get.name].data[0];
        const { price, is_active } = res;
        if (res) {
          setValue(Number(price || 0));
          setIsActive(Boolean(is_active));
        }
      }
    } catch (error) {}
  };

  const onSubmit = async (isActive = true) => {
    try {
      const { data, errors } = await updateModel({
        context: {
          serviceName: graph.create.serviceName,
          headers: {
            authorization: `Bearer ${userToken}`,
          },
        },
        variables: {
          price: value ? String(value) : "0",
          action_id: String(id),
          is_active: Number(isActive),
        },
      });
      if (!errors) {
        if (!isEmptyObject(data)) {
          data[graph.create.name]?.messages.map((message) =>
            toast.success(String(message))
          );
          setIsActive(isActive);
          setUpdated(true);
          setDisabled(true);
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    getModel();
  }, []);

  return (
    <>
      {disabled ? (
        <Card variant="outlined" sx={{ position: "relative" }}>
          <CardActionArea onClick={onClick}>
            <CardHeader
              sx={{ p: 1, height: 56, width: "100%" }}
              title={
                value ? (
                  <Typography
                    sx={{ mx: 0.5 }}
                    variant="subtitle1"
                    color="text.primary"
                  >
                    {addCommas(digitsFaToEn(String(Number(value))))}
                  </Typography>
                ) : (
                  <Typography
                    sx={{ mx: 0.5 }}
                    variant="subtitle1"
                    color="text.disabled"
                  >
                    تعیین نشده
                  </Typography>
                )
              }
            />
          </CardActionArea>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ position: "absolute", right: 0, top: 0, bottom: 0, px: 1 }}
          >
            {value > 0 && <Chip sx={{ mx: 0.5 }} size="small" label="تومان" />}
            <IconButton
              size="small"
              color="warning"
              aria-label="edit"
              onClick={onClick}
              loading={loading || updating}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
            <Divider sx={{ height: 28 }} orientation="vertical" />
            <IOSSwitch
              size="small"
              sx={{ mx: 0.5 }}
              checked={isActive}
              disabled={loading || updating}
              onChange={() => onSubmit(!isActive)}
              inputProps={{ "aria-label": "controlled" }}
            />
          </Stack>
        </Card>
      ) : (
        <Card
          variant="outlined"
          component="form"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <CardHeader
            sx={{ p: 1, height: 56, width: "100%" }}
            title={
              <InputBase
                value={value}
                type="number"
                placeholder="Price"
                onChange={onChange}
                disabled={disabled}
                inputProps={{ "aria-label": "price" }}
                sx={{
                  ml: 1,
                  flex: 1,
                  fontSize: 16,
                  fontWeight: "bold",
                  letterSpacing: 2,
                }}
              />
            }
            action={
              <Stack direction="row" alignItems="center">
                <Chip
                  disabled={disabled}
                  sx={{ mx: 0.5 }}
                  size="small"
                  label="تومان"
                />
                <Divider sx={{ height: 28 }} orientation="vertical" />
                <IconButton
                  size="small"
                  aria-label="price"
                  loading={loading || updating}
                  disabled={loading || updating}
                  onClick={() => onSubmit(true)}
                  color={updated ? "success" : "primary"}
                >
                  <CheckCircleRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={onClick}
                  size="small"
                  color="error"
                  aria-label="cancel"
                >
                  <CancelRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          />
        </Card>
      )}
    </>
  );
}

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));
